/**
 * UI permission-keys for the `INVOICE` route (the Invoices module).
 *
 * Typed mirror of the BE CSV — the BE is the source of truth, and it authorizes every real call
 * regardless of what is rendered here. These keys only decide which controls are OFFERED.
 *
 * Two different mechanisms are mirrored here, and the difference matters:
 *
 *   `invoices:*`  FE-side namespace. Resolved through acl-svc `myPermissions('INVOICE')`, so each
 *                 needs a row in `apps/acl-svc/config/ui-permissions.csv` before it resolves at all
 *                 (fail-closed ⇒ an unseeded key hides its control). See the BE handover under
 *                 `docs/handover/be/invoices-fe/`.
 *
 *   `invoice:*`   NOT acl-svc permissions. These are Keycloak REALM ROLES, forwarded to the
 *                 subgraph as `x-user-roles` and checked directly by the invoice-svc guards
 *                 (`BankFileGenerateGuard`, the `AclGuard` mixin, the money-movement operation
 *                 gate). Listing them here lets the UI hide a control the server would refuse, but
 *                 the string is a role, not a (module, action) pair — a ui-permissions row for one
 *                 must be persona-only (blank module/action) or the import will reject it.
 */
export const InvoiceAclKeys = {
  /** Access to the /invoices listing + detail subtree. */
  listingRead: 'invoices:listing:read',
  /**
   * Raise an invoice — gates the header action and the button-level `<Can>` wraps on the create page.
   *
   * ⚠️ It does NOT gate the `/invoices/new` ROUTE. This comment claimed it did; that was false.
   * `src/routes/invoices/new.tsx` is `createFileRoute('/invoices/new')({})` — no `beforeLoad`, no
   * guard — so any authenticated persona can navigate directly to the page and only the buttons are
   * hidden. The missing route guard is a known open gap, raised separately; ADR-012 records that a
   * persona affordance gate is not a substitute for it. Do not read this key as route protection.
   */
  raise: 'invoices:raise',
  /** Approve / reject a received invoice. Reject shares this key (same approver personas). */
  approve: 'invoices:approve',
  /** Place and release holds. */
  hold: 'invoices:hold',
  /**
   * Settle / mark-paid a received invoice. Backs the "Mark as paid" action, which drives the
   * simulated `performInvoiceOperations(PAY)` → `APPROVED → PAID` transition (no wallet peer needed).
   * Already seeded in `apps/acl-svc/config/ui-permissions.csv` (`INVOICE,invoices:pay,…`, personas
   * SA/AGENCY/CLIENT — the receiving party). Server-side invoice-svc still fail-closes any real call
   * to the receiving persona (`INV_NOT_INVOICE_PAYER`), so this key only decides whether the control
   * is offered.
   */
  pay: 'invoices:pay',
  /**
   * Void an invoice (destructive, terminal).
   * Mapped to `invoice:void` (`ui-permissions.csv` row 123, personas PLATFORM_ADMIN/AGENCY) by
   * entity-backend M3-T20 (ENT-566), per the UI-key mapping report
   * (`entity-backend/docs/sdlc/reports/task-first-f1-m3/ui-key-mapping-2026-10-21.md`).
   */
  void: 'invoices:void',
  /**
   * Create a local invoice record from an uploaded document ("Create from document").
   * ENT-566 T0253 (round-5 finding) — this control was gated ONLY by the raw persona-literal
   * `canImportInvoice` in `persona-config.ts` (never through `usePermissions`/ACL at all), unlike
   * its sibling "Raise invoice" button which also wraps `<Can k={InvoiceAclKeys.raise}>`. Cannot
   * reuse `raise`: `canImportInvoice` and `canRaise` deliberately disagree per persona (CLIENT
   * can import but not raise; VENDOR can raise but must NOT import — see persona-config.ts's
   * own `canCopyPreviousInvoice` comment for the same distinction).
   * Seeded by entity-backend M3-T20 round-5 slice (ENT-566, `INVOICE,invoices:import-document,,,
   * YES,YES,YES,NO,NO`), persona-only — `createInvoices` carries no `(module, action)` gate to map
   * onto (see entity-backend's round-5 UI-keys handover). An acting-as session still sees this
   * control until BE's read/write marker slice lands; the real call is refused server-side either
   * way (`assertNotActingAs`).
   */
  importDocument: 'invoices:import-document',

  // ── keys that mirror a real server-side acl gate ──────────────────
  /**
   * Manage the tenant's custom-field definition catalogue.
   * Server gate on `upsertInvoiceCustomFieldDefinition` / `archiveInvoiceCustomFieldDefinition`.
   */
  customFieldsAdmin: 'invoice:custom-fields-admin',
  /**
   * Manage the tenant's catalogue overrides — line-item categories, service-catalogue entries,
   * tax rules.
   * Server gate on `upsertInvoiceLineItemCategory` / `archiveInvoiceLineItemCategory` /
   * `upsertInvoiceServiceCatalogueEntry` / `archiveInvoiceServiceCatalogueEntry` /
   * `upsertInvoiceTaxRule` / `archiveInvoiceTaxRule`.
   */
  catalogueAdmin: 'invoice:catalogue-admin',
  /** Server gate on `generateBankFile` (BankFileGenerateGuard, fail-closed). */
  bankFileGenerate: 'invoice:bank-file-generate',
  /** Server operation-gate on `triggerInvoicesBulkPayout`. DARK today — peer not activated. */
  bulkPayout: 'invoice:bulk-payout',
  /** Server operation-gate on `disburseInvoice`. DARK today — peer not activated. */
  disburse: 'invoice:disburse',
} as const;

export type InvoiceAclKey = (typeof InvoiceAclKeys)[keyof typeof InvoiceAclKeys];
